import React, { useCallback, useContext, useEffect, useState } from "react";
import { Formik } from "formik";
import * as yup from "yup";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Image from "react-bootstrap/Image";
import Spinner from "react-bootstrap/Spinner";
import { format, parseISO } from "date-fns";
import { AppContext } from "../../App";
import CcEmailsField from "../../components/Settings/CcEmailsField";
import {
  getFinanceReceptionEmailSettings,
  updateFinanceReceptionEmailSettings,
} from "../../clients/finance_reception_email_client";
import { emailRegex, largeLogo } from "../../config_and_helpers/config";
import { showToast } from "../../config_and_helpers/helpers";
import { logInfo } from "../../config_and_helpers/logger";
import GlassWorkspaceShell from "../../components/Glass/GlassWorkspaceShell";

const LOG_CONTEXT = "FinanceReceptionEmailPage";

const initialValues = {
  recipient_email: "",
  recipient_name: "",
  cc_emails: [],
};

const validationSchema = yup.object().shape({
  recipient_email: yup
    .string()
    .trim()
    .test(
      "valid-recipient",
      "Enter a valid email",
      (value) => !value || emailRegex.test(value),
    ),
  recipient_name: yup
    .string()
    .trim()
    .max(100, "Name must be 100 characters or less"),
  cc_emails: yup
    .array()
    .of(
      yup
        .string()
        .trim()
        .test(
          "valid-cc",
          "Enter a valid email",
          (value) => !value || emailRegex.test(value),
        ),
    ),
});

/**
 * Formats API `updated_at` for display.
 *
 * @param {string|null|undefined} updatedAt - ISO 8601 datetime from API.
 * @returns {string}
 */
const formatUpdatedAt = (updatedAt) => {
  if (!updatedAt) return "";
  try {
    return format(parseISO(updatedAt), "dd/MM/yyyy HH:mm");
  } catch {
    return updatedAt;
  }
};

/**
 * Joins DRF field error arrays into a single string.
 *
 * @param {string|string[]|undefined} fieldError
 * @returns {string|undefined}
 */
const joinFieldError = (fieldError) => {
  if (!fieldError) return undefined;
  return Array.isArray(fieldError) ? fieldError.join(" ") : fieldError;
};

/**
 * Settings page for finance daily order-reception digest recipients.
 */
const FinanceReceptionEmailPage = () => {
  const { token } = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formInitialValues, setFormInitialValues] = useState(initialValues);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    logInfo(LOG_CONTEXT, "loadSettings start", {});

    const result = await getFinanceReceptionEmailSettings(token);

    if (result.success) {
      const { recipient_email, recipient_name, cc_emails, updated_at } =
        result.data;
      setFormInitialValues({
        recipient_email: recipient_email ?? "",
        recipient_name: recipient_name ?? "",
        cc_emails: cc_emails ?? [],
      });
      setUpdatedAt(updated_at ?? null);
    } else {
      setLoadError("Could not load settings. Please try again.");
      showToast(
        "Could not load finance reception email settings.",
        "error",
        "top-right",
        3000,
      );
    }

    setIsLoading(false);
  }, [token]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSubmit = async (values, { setErrors }) => {
    setIsSubmitting(true);

    const payload = {
      recipient_email: values.recipient_email.trim(),
      recipient_name: values.recipient_name.trim(),
      cc_emails: values.cc_emails
        .map((email) => email.trim())
        .filter((email) => email.length > 0),
    };

    logInfo(LOG_CONTEXT, "handleSubmit", {
      hasRecipient: Boolean(payload.recipient_email),
      hasRecipientName: Boolean(payload.recipient_name),
      ccCount: payload.cc_emails.length,
    });

    const result = await updateFinanceReceptionEmailSettings(token, payload);

    if (result.success) {
      const { recipient_email, recipient_name, cc_emails, updated_at } =
        result.data;
      setFormInitialValues({
        recipient_email: recipient_email ?? "",
        recipient_name: recipient_name ?? "",
        cc_emails: cc_emails ?? [],
      });
      setUpdatedAt(updated_at ?? null);
      result.toast();
    } else if (result.fieldErrors) {
      const nextErrors = {};
      const recipientError = joinFieldError(result.fieldErrors.recipient_email);
      const recipientNameError = joinFieldError(
        result.fieldErrors.recipient_name,
      );
      const ccError = joinFieldError(result.fieldErrors.cc_emails);

      if (recipientError) {
        nextErrors.recipient_email = recipientError;
      }
      if (recipientNameError) {
        nextErrors.recipient_name = recipientNameError;
      }
      if (ccError) {
        nextErrors.cc_emails = ccError;
      }

      if (Object.keys(nextErrors).length > 0) {
        setErrors(nextErrors);
      } else {
        showToast(
          "Could not save settings. Please check your input.",
          "error",
          "top-right",
          3000,
        );
      }
    } else {
      showToast(
        "An unexpected error occurred. Please try again.",
        "error",
        "top-right",
        3000,
      );
    }

    setIsSubmitting(false);
  };

  if (isLoading) {
    return (
      <GlassWorkspaceShell theme="yellow">
        <div className="py-5 text-center">
          <Spinner animation="border" role="status" />
        </div>
      </GlassWorkspaceShell>
    );
  }

  return (
    <GlassWorkspaceShell theme="yellow">
    <div className="glass-panel" style={{ maxWidth: "640px", margin: "0 auto" }}>
      <div className="text-center mb-4">
        <Image src={largeLogo} style={{ width: "30%", marginBottom: "10px" }} />
        <h1 className="glass-page-heading">Finance reception email</h1>
      </div>

      {loadError && (
        <Alert variant="danger" className="mb-3">
          {loadError}
        </Alert>
      )}

      <Formik
        initialValues={formInitialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formik) => {
          const {
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit: submitForm,
          } = formik;
          const showPausedWarning = !values.recipient_email?.trim();

          return (
            <Form noValidate onSubmit={submitForm}>
              {showPausedWarning && (
                <Alert variant="warning" className="mb-3">
                  Daily finance emails are paused until a recipient is set.
                </Alert>
              )}

              {updatedAt && (
                <p className="text-muted small mb-3">
                  Last saved: {formatUpdatedAt(updatedAt)}
                </p>
              )}

              <Form.Group controlId="recipient_email" className="mb-3">
                <Form.Label>Primary recipient (To)</Form.Label>
                <Form.Text className="d-block mb-2 text-muted">
                  External finance contact who receives the daily reception
                  summary.
                </Form.Text>
                <Form.Control
                  name="recipient_email"
                  type="email"
                  value={values.recipient_email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={
                    Boolean(errors.recipient_email) && touched.recipient_email
                  }
                />
                {errors.recipient_email && touched.recipient_email && (
                  <Form.Control.Feedback type="invalid">
                    {errors.recipient_email}
                  </Form.Control.Feedback>
                )}
              </Form.Group>

              <Form.Group controlId="recipient_name" className="mb-3">
                <Form.Label>Recipient name (שם נמען)</Form.Label>
                <Form.Text className="d-block mb-2 text-muted">
                  Optional first or display name for the digest greeting (e.g.
                  שלום ישראל,). Leave empty for a generic שלום, only.
                </Form.Text>
                <Form.Control
                  name="recipient_name"
                  type="text"
                  maxLength={100}
                  value={values.recipient_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={
                    Boolean(errors.recipient_name) && touched.recipient_name
                  }
                />
                {errors.recipient_name && touched.recipient_name && (
                  <Form.Control.Feedback type="invalid">
                    {errors.recipient_name}
                  </Form.Control.Feedback>
                )}
              </Form.Group>

              <CcEmailsField formik={formik} />

              <div className="mt-4">
                {isSubmitting ? (
                  <Button variant="primary" disabled>
                    <Spinner
                      size="sm"
                      as="span"
                      animation="border"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Saving...
                  </Button>
                ) : (
                  <Button variant="primary" type="submit">
                    Save
                  </Button>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
    </GlassWorkspaceShell>
  );
};

export default FinanceReceptionEmailPage;
